import { useCallback, useMemo, useState } from "react"
import { ready } from "zpl-renderer-js"
import { useShallow } from "zustand/shallow"

import { toGoodsReceiptPalletLabelParams } from "../utils"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useZplPrinting } from "@/hooks/useZplPrinting"
import { NotificationType } from "@/schemas/notification"
import { useGetPalletByReferenceId } from "@/screens/Tablet/hooks/useGetPalletByReferenceId"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore.ts"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { generatePalletLabelZpl } from "@/utils/printing/generatePalletLabelZpl"

interface UseGoodsReceiptPalletLabelPrintingReturn {
  printPalletLabel: (referenceId: number, preview?: boolean) => Promise<boolean>
  printPreview: () => Promise<void>
  closeModal: () => void
  isModalOpen: boolean
  previewImageData: string | null
  isFetching: boolean
  isPrinting: boolean
  isGeneratingPreview: boolean
  canPrint: boolean
}

export const useGoodsReceiptPalletLabelPrinting = (): UseGoodsReceiptPalletLabelPrintingReturn => {
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [previewImageData, setPreviewImageData] = useState<string | null>(null)
  const [currentPallet, setCurrentPallet] = useState<GoodsReceiptPallet | null>(null)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)

  const { fetchPallet, isFetching } = useGetPalletByReferenceId()
  const { printZpl } = useZplPrinting()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const zplCode = useMemo(() => {
    if (!currentPallet) return null
    return generatePalletLabelZpl(toGoodsReceiptPalletLabelParams(currentPallet, uoms))
  }, [currentPallet, uoms])

  const generatePreview = useCallback(
    async (zpl: string): Promise<string | null> => {
      setIsGeneratingPreview(true)

      try {
        const { api } = await ready
        const base64Image = await api.zplToBase64Async(zpl)
        const imageData = `data:image/png;base64,${base64Image}`
        setPreviewImageData(imageData)
        return imageData
      } catch (error) {
        console.error(error ?? "Error generating preview:", error)
        notify({
          type: NotificationType.Error,
          message: error?.toString() ?? "Failed to generate label preview",
        })
        return null
      } finally {
        setIsGeneratingPreview(false)
      }
    },
    [notify]
  )

  const printPalletLabel = useCallback(
    async (referenceId: number, preview = false): Promise<boolean> => {
      setIsPrinting(true)

      try {
        const pallet = await fetchPallet(referenceId)
        if (!pallet) {
          notify({
            type: NotificationType.Error,
            message: "Failed to load pallet data",
          })
          return false
        }

        setCurrentPallet(pallet)
        const zpl = generatePalletLabelZpl(toGoodsReceiptPalletLabelParams(pallet, uoms))

        if (preview) {
          await generatePreview(zpl)
          setIsModalOpen(true)
          return true
        } else {
          return await printZpl(zpl)
        }
      } catch (error) {
        console.error("Error in print function:", error)
        notify({
          type: NotificationType.Error,
          message: "Failed to process print request",
        })
        return false
      } finally {
        setIsPrinting(false)
      }
    },
    [fetchPallet, notify, uoms, generatePreview, printZpl]
  )

  const printPreview = useCallback(async () => {
    if (!zplCode) return

    setIsPrinting(true)
    try {
      const success = await printZpl(zplCode)
      if (success) {
        setIsModalOpen(false)
      }
    } finally {
      setIsPrinting(false)
    }
  }, [zplCode, printZpl])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return {
    printPalletLabel,
    printPreview,
    closeModal,
    isModalOpen,
    previewImageData,
    isFetching,
    isPrinting,
    isGeneratingPreview,
    canPrint: !!zplCode,
  }
}
